import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api";
import SEO from "../components/SEO";

export function Blog() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)).catch(() => {}); }, []);
  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-4xl mx-auto py-12 md:py-20">
      <SEO title="Rental guides & Vancouver market insights — Tendly Blog" description="Practical guides for renting in Vancouver: neighbourhood rents, tenant rights, and how to win the home you want." />
      <p className="text-xs uppercase tracking-widest font-semibold text-primary/80">Blog</p>
      <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tight">Rental guides for Vancouver</h1>
      {posts.length === 0 && (
        <div className="mt-10 rounded-2xl border border-dashed border-border p-10 text-center" data-testid="blog-empty">
          <p className="font-semibold">Guides coming soon</p>
          <p className="mt-2 text-sm text-muted-foreground">We're writing practical rental guides for Vancouver. Check back shortly.</p>
        </div>
      )}
      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        {posts.map((p) => (
          <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`}
            className="rounded-2xl border border-border bg-card p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all">
            {p.cover_image && <img src={p.cover_image} alt={p.title} data-testid={`blog-card-image-${p.slug}`} loading="lazy"
              className="mb-4 h-40 w-full object-cover rounded-xl border border-border" />}
            <div className="flex gap-2 mb-3">
              {(p.tags || []).map((t) => <span key={t} className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-secondary-foreground">{t}</span>)}
            </div>
            <h2 className="font-heading font-semibold text-xl tracking-tight">{p.title}</h2>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{p.excerpt}</p>
            <p className="mt-3 text-xs text-muted-foreground">{p.author} · {p.published_at?.slice(0, 10)}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setNotFound(true)); }, [slug]);
  if (notFound) return <div className="max-w-2xl mx-auto py-24 text-center"><h1 className="text-2xl font-bold">Post not found</h1><Link to="/blog" className="mt-4 inline-block text-primary underline">Back to blog</Link></div>;
  if (!post) return <div className="py-24 text-center text-muted-foreground">Loading…</div>;
  return (
    <div className="px-4 sm:px-6 lg:px-12 max-w-3xl mx-auto py-12 md:py-20">
      <SEO title={`${post.title} | Tendly`} description={post.excerpt}
        image={post.cover_image ? new URL(post.cover_image, window.location.origin).href : undefined}
        jsonLd={{ "@context": "https://schema.org", "@type": "Article", headline: post.title, description: post.excerpt, author: { "@type": "Organization", name: "Tendly" }, datePublished: post.published_at,
          ...(post.cover_image ? { image: new URL(post.cover_image, window.location.origin).href } : {}) }} />
      <nav className="text-xs text-muted-foreground mb-6"><Link to="/blog" className="hover:text-primary">Blog</Link> / <span className="text-foreground">{post.title}</span></nav>
      <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">{post.title}</h1>
      <p className="mt-3 text-sm text-muted-foreground">Written by {post.author || "Tendly Team"} · {post.published_at?.slice(0, 10)}{post.visuals_by ? ` · Visuals by ${post.visuals_by}` : ""}</p>
      {post.cover_image && <img src={post.cover_image} alt={post.title} data-testid="blog-cover-image" loading="lazy"
        className="mt-8 w-full rounded-2xl border border-border shadow-sm" />}
      {(post.body || "").trim().startsWith("<") ? (
        <div data-testid="blog-body" className="blog-body mt-8 leading-relaxed"
          dangerouslySetInnerHTML={{ __html: post.body }} />
      ) : (
        <div className="mt-8 space-y-4 text-black dark:text-foreground leading-relaxed">
          {(post.body || "").split("\n\n").map((para, i) => {
            const img = para.trim().match(/^!\[(.*?)\]\((.+?)\)$/);
            if (img) return <img key={i} data-testid="blog-body-image" src={img[2]} alt={img[1]} loading="lazy"
              className="w-full rounded-2xl border border-border shadow-sm" />;
            return <p key={i} dangerouslySetInnerHTML={{ __html: para.replace(/\*\*(.+?)\*\*/g, '<strong class="text-black dark:text-foreground">$1</strong>').replace(/\n/g, "<br/>") }} />;
          })}
        </div>
      )}
      <div className="mt-12 rounded-2xl bg-primary text-primary-foreground p-8">
        <h2 className="font-heading font-semibold text-xl">Searching right now?</h2>
        <p className="mt-2 text-sm text-primary-foreground/85">Let Tendly match you with live listings that fit your exact criteria — free.</p>
        <Link to="/chat" data-testid="blog-open-chat" className="mt-5 inline-block rounded-full bg-accent text-accent-foreground px-6 py-2.5 text-sm font-semibold hover:bg-coral-600 transition-colors">Open live chat</Link>
      </div>
    </div>
  );
}
